import {
  MiddlewareAPI,
  isRejectedWithValue,
  Middleware
} from '@reduxjs/toolkit';
import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query/react';
import { acquireLock, isLocked, releaseLock } from 'cs-delta-utils-react';
import { ApiError, ApiErrorDto } from 'models/errors/ApiError';
import { MetaForward } from 'models/jsonApi';
import { displayApiError } from 'store/globalErrorState';
import { logout, logoutSuccess } from 'store/authState';
import { store } from 'store';
import * as config from 'config';

import { refreshToken } from 'services/authService';
import { isEmptyString } from 'utils/stringUtils';

export const defaultHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json'
};

/**
 * Parse errors
 */
export function parseJsonError(json: any, fallbackText?: string): ApiError {
  // RTK Query errors may have no payload.
  const errors = json?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    // Assume JSON:API standard error
    const [first] = errors;

    // Check if token expired
    if (first.status === 403 || first.status === 401) {
      store.dispatch(logoutSuccess());
    }

    return new ApiErrorDto(first.title, {
      code: first.code,
      status: first.status
    }).toJson();
  }
  console.warn('Unknown error response:', json);
  return new ApiErrorDto(json?.message ?? fallbackText).toJson();
}

/**
 * Base query
 */
const baseQuery = fetchBaseQuery({
  baseUrl: config.backendApi,
  credentials: 'include'
});

/**
 * Reauth base query
 */
export const baseQueryAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (!result.error) {
    return result;
  }

  const apiError = parseJsonError(result.error?.data);
  const expiredTokenError =
    apiError.status === 401 && apiError.code === 'expired.token';

  // Expired token error
  if (expiredTokenError) {
    // Acquire lock and continue on the first request, or wait for the lock to be released
    await acquireLock();

    // Try refreshing the session and release the lock
    if (isLocked()) {
      const refreshed = await refreshToken();
      if (!refreshed) {
        api.dispatch(logout());
      }
      releaseLock();
    }

    // Retry initial query
    result = await baseQuery(args, api, extraOptions);
  }

  return result;
};

/**
 * Log errors and display toasts
 */
export const rtkQueryError: Middleware =
  (api: MiddlewareAPI) => (next) => (action: any) => {
    if (isRejectedWithValue(action)) {
      const errors = action.payload?.data?.errors;
      if (errors?.length) {
        errors.forEach((error: any) => {
          api.dispatch(
            displayApiError(
              new ApiErrorDto(error.title, {
                code: error.code,
                status: error.status
              }).toJson()
            )
          );
        });
      }
    }
    return next(action);
  };

export function createFilterUrl<T extends Record<string, string | null>>(
  url: string,
  filterObj: T
): string {
  const filters = Object.entries(filterObj).filter(
    ([_, value]) => value?.length
  );

  filters.forEach(([key, value], i) => {
    if (!value || isEmptyString(value)) {
      return;
    }

    if (i === 0) {
      url += '?';
    }

    url += `filter[${key}]=${encodeURIComponent(value)}`;

    if (filters.length !== i + 1) {
      url += '&';
    }
  });

  return url;
}

export function createQueryUrl<
  T extends Record<string, string | null | undefined>
>(url: string, queryObj: T): string {
  const queries = Object.entries(queryObj).filter(
    ([_, value]) => value?.length
  );

  queries.forEach(([key, value], i) => {
    if (!value || isEmptyString(value)) {
      return;
    }
    if (i === 0) {
      url += '?';
    }
    url += `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    if (queries.length !== i + 1) {
      url += '&';
    }
  });

  return url;
}

export async function apiFetch<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  // Fetch
  const res = await fetch(url, { credentials: 'include', ...options });

  const text = await res.text();

  // No response body
  if (!text) {
    return null as unknown as T;
  }

  // JSON
  const json = await JSON.parse(text);

  // Success
  if (res.ok) {
    return json;
  }

  console.warn(
    'Failed - statusText: %s, status: %d',
    res.statusText,
    res.status
  );

  const apiError = parseJsonError(json, res.statusText);
  const expiredTokenError =
    apiError.status === 401 && apiError.code === 'expired.token';

  // Expired token error
  if (expiredTokenError) {
    // Acquire lock and continue on the first request, or wait for the lock to be released
    await acquireLock();

    // Try refreshing the session and release the lock
    if (isLocked()) {
      const refreshed = await refreshToken();
      if (!refreshed) {
        store.dispatch(logout());
      }
      releaseLock();
    }

    // Retry initial query
    return apiFetch(url, options);
  }

  throw apiError;
}

/**
 * Text defaults to Husqvarna MyPages or Husqvarna Construction MyPages based on the brand.
 */
export function buildMetaForward(
  path: string,
  text = `${config.isHusqvarna ? 'Husqvarna MyPages' : 'Husqvarna Construction MyPages'}`
): MetaForward {
  const pathWithLeadingSlash = path.startsWith('/') ? path : `/${path}`;
  return {
    uri: `${window.location.origin}${pathWithLeadingSlash}`,
    text
  };
}
